'use client'

// Client-side filtering UI for /search. Hoisted out of page.tsx so the
// route can be a server component that fetches the permitted-river-ID
// set once at request time and passes it down. Permit badge rendering
// is otherwise impossible without either a per-row fetch (slow) or a
// stale static list.

import { useState, useMemo } from 'react'
import Link from 'next/link'
import { ALL_RIVERS, getRiverPath } from '@/data/rivers'
import { RAPIDS } from '@/data/rapids'

interface Props {
  // Set is serializable across the server/client boundary as a plain
  // array; the server route passes it as string[] and we rebuild the
  // Set here for O(1) lookup inside the result loop.
  permittedRiverIds: string[]
}

export default function SearchClient({ permittedRiverIds }: Props) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<'all' | 'ww' | 'wild'>('all')

  const permitSet = useMemo(() => new Set(permittedRiverIds), [permittedRiverIds])

  const results = useMemo(() => {
    const q = query.toLowerCase().trim()

    // Ranker (2026-07-17): score-based, no description dilution.
    //
    // Earlier (2026-07-10): name matches ranked above description matches,
    // but description matches were still returned alongside — for a 3-letter
    // query like "fri" that produced 10+ irrelevant rivers (anything whose
    // `desc` happened to contain "fri"). Frio River got buried at the bottom.
    //
    // Now: every match gets a score. Name matches always beat description
    // matches. Description matches are ranked by where the substring falls
    // (earlier in the text = more relevant). Total results capped at 20 so
    // the list doesn't flood the screen on a 2-letter query.
    //
    //   Name match on full name           = 1000
    //   Name match on a word boundary     = 500
    //   County / designation match        = 200
    //   Description match (earlier=better)= 100 - (position / 50)
    //                                     = 0 at position 5000+
    type Scored = { river: typeof ALL_RIVERS[number]; score: number }
    const scored: Scored[] = []
    for (const r of ALL_RIVERS) {
      const name = r.n.toLowerCase()
      const county = (r.co as string).toLowerCase()
      const desig = (r.desig as string).toLowerCase()
      const desc = (r.desc as string).toLowerCase()

      let score = 0

      if (!q) {
        score = 0
      } else if (name === q) {
        score = 1000
      } else if (name.startsWith(q)) {
        score = 800
      } else if (name.includes(' ' + q) || name.includes(q + ' ') || name.includes('-' + q) || name.includes(q + '-')) {
        // word-boundary partial — "Frio River" matches "frio" or "rio" or "fri"
        score = 500
      } else if (name.includes(q)) {
        // substring (less relevant)
        score = 100
      } else if (county.includes(q) || desig.includes(q)) {
        score = 200
      } else {
        const pos = desc.indexOf(q)
        if (pos >= 0) {
          // earlier position = higher score
          score = Math.max(5, 100 - Math.floor(pos / 50))
        }
      }

      if (score > 0) {
        scored.push({ river: r, score })
      }
    }

    // Stable sort: score desc, then catalog order (preserves existing
    // deterministic behavior — same query = same order)
    scored.sort((a, b) => b.score - a.score)

    return scored.slice(0, 20).map(s => s.river)
  }, [query])

  // Apply filter buttons (all / whitewater / wild & scenic) on top of the
  // ranked text search. Done as a separate pass so the text ranking stays
  // stable across filter changes.
  const filteredResults = useMemo(() => {
    if (filter === 'all') return results
    return results.filter(r => {
      const rec = r as Record<string, unknown>
      if (filter === 'ww') return !!rec['ww']
      if (filter === 'wild') return !!rec['wild']
      return true
    })
  }, [results, filter])

  return (
    <main style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--tx)', display: 'flex', flexDirection: 'column' }}>
      {/* Search header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--bd)', flexShrink: 0 }}>
        <input
          type="text"
          placeholder="Search rivers, states, designations…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
          style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd2)', borderRadius: 'var(--r)', fontSize: '14px', fontFamily: "'IBM Plex Mono', monospace", background: 'var(--bg)', color: 'var(--tx)', outline: 'none' }}
        />
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
          {(['all', 'ww', 'wild'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", padding: '3px 8px',
                border: '1px solid var(--bd2)', borderRadius: '12px', cursor: 'pointer',
                background: filter === f ? 'var(--rvlt)' : 'var(--bg2)',
                color: filter === f ? 'var(--rvdk)' : 'var(--tx2)',
                borderColor: filter === f ? 'var(--rvmd)' : 'var(--bd2)',
              }}
            >
              {f === 'all' ? 'All Rivers' : f === 'ww' ? 'Whitewater' : 'Wild & Scenic'}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: 'var(--tx3)', padding: '8px 24px', textTransform: 'uppercase', letterSpacing: '.5px' }}>
          {filteredResults.length} rivers
        </div>
        {filteredResults.map(river => (
          <Link
            key={river.id}
            href={getRiverPath(river)}
            style={{ display: 'block', padding: '9px 24px', borderBottom: '1px solid var(--bd)', textDecoration: 'none', color: 'var(--tx)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontFamily: "'Playfair Display', serif", fontSize: '14px', fontWeight: 600 }}>
                  {river.n}
                  {RAPIDS[river.id]?.length > 0 && (
                    <span className="verified-badge" title="Rapids verified by local paddlers">&#10003;</span>
                  )}
                </div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: 'var(--tx2)', margin: '1px 0' }}>
                  {river.stateName} · {river.co}
                </div>
                <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", padding: '2px 5px', borderRadius: '3px', background: 'var(--wtlt)', color: 'var(--wt)' }}>
                    Class {river.cls}
                  </span>
                  <span style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", color: 'var(--tx3)' }}>{river.len}</span>
                  {!!(river as Record<string, unknown>)['wild'] && <span style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", padding: '2px 5px', borderRadius: '3px', background: '#EAF3DE', color: '#3B6D11' }}>Wild & Scenic</span>}
                  {permitSet.has(river.id) && (
                    <span
                      title="Permit required — see river page for details."
                      style={{
                        fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace",
                        padding: '2px 5px', borderRadius: '3px',
                        color: '#7A4D0E', background: 'var(--amlt)',
                        border: '1px solid var(--am)',
                        fontWeight: 600, letterSpacing: '.3px',
                      }}>
                      PERMIT
                    </span>
                  )}
                </div>
              </div>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--tx2)', marginTop: '4px', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {river.desc as string}
            </p>
          </Link>
        ))}
      </div>
    </main>
  )
}
